
import {
  defineComponent,
  ref,
  toRefs,
  computed,
  watch,
  ComputedRef,
} from "vue";
import { countryList } from "./map_country";
import LoadingModal from "./LoadingModal.vue";
import StatusBlock from "./StatusBlock.vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import { States, submitHandler } from "./submit_handler";
import { useUserStore } from "@/store/user";
import VueCardValidator from "./CardValidatorForm.vue";
import { useConfigStore } from "@/store/config";

export default defineComponent({
  components: {
    Form,
    Field,
    ErrorMessage,
    StatusBlock,
    LoadingModal,
    VueCardValidator,
  },
  props: {
    changeEnabled: {
      type: Boolean,
      required: true,
    },
    changeCancelEnabled: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["billingInfoUpdated", "update:changeEnabled"],
  setup(props, { emit }) {
    const user = useUserStore();
    const config = useConfigStore();
    let states: States = {
      recaptcha: null,
      loadingShow: ref(false),
      errorMessage: ref(""),
      captchaEnabled: false,
    };

    const token = ref<OpaqueTokenData | null>(null);
    const cardNumber = ref("");
    const cardExpiration = ref("");
    const cardCVC = ref("");

    let { changeEnabled } = toRefs(props);

    const bi: ComputedRef<any> = computed(() => {
      return user.billingInfo;
    });
    let billingInfo = ref(prepareBillingInfo(bi));
    const cancelDisabled = ref(bi.value === null);

    function prepareBillingInfo(bi: ComputedRef<any>) {
      //TODO ADD TYPE
      return {
        firstName: bi.value?.firstName ? bi.value.firstName : "",
        lastName: bi.value?.lastName ? bi.value.lastName : "",
        paymentMethod: {
          lastFour: bi.value?.paymentMethod?.lastFour,
          expMonth: bi.value?.paymentMethod?.expMonth
            ? parseInt(bi.value.paymentMethod.expMonth, 10)
            : "",
          expYear: bi.value?.paymentMethod?.expYear
            ? parseInt(bi.value.paymentMethod.expYear, 10)
            : "",
          maskedCardNumber:
            bi.value?.paymentMethod?.lastFour &&
              bi.value?.paymentMethod?.expMonth &&
              bi.value?.paymentMethod?.expYear
              ? `•••• •••• •••• ${bi.value.paymentMethod.lastFour}    ${bi.value.paymentMethod.expMonth}/${bi.value.paymentMethod.expYear}`
              : "",
        },
        address: {
          country: bi.value?.address?.country ? bi.value.address.country : "",
          street1: bi.value?.address?.street1 ? bi.value.address.street1 : "",
          street2: bi.value?.address?.street2 ? bi.value.address.street2 : "",
          city: bi.value?.address?.city ? bi.value.address.city : "",
          region: bi.value?.address?.region ? bi.value.address.region : "",
          postalCode: bi.value?.address?.postalCode
            ? bi.value.address.postalCode
            : "",
        },
      };
    }

    watch(bi, () => {
      cancelDisabled.value = bi.value === null;
      billingInfo.value = prepareBillingInfo(bi);
    });

    async function updateBillingInfo() {
      await submitHandler(tokenizeCard(), states, (tokenData) => {
        token.value = tokenData;
        user.setBillingInfo(cardForm2BillingInfo());
        cardNumber.value = "";
        cardExpiration.value = "";
        cardCVC.value = "";
        toggleChangeEnabled();
      });

      if (token.value) {
        await submitHandler(user.updateCard({ token: token.value.dataValue }), states, () => {
          emit("billingInfoUpdated");
        });
      }
    }

    function toggleChangeEnabled() {
      changeEnabled.value = !changeEnabled.value;
      emit("update:changeEnabled", changeEnabled.value);
    }

    interface OpaqueTokenData {
      dataDescriptor: string;
      dataValue: string;
    }
    interface AuthorizeNetResponse {
      opaqueData: OpaqueTokenData;
      messages: {
        resultCode: "Error" | "Ok";
        message: {
          code: string;
          text: string;
        }[];
      };
    }

    function cardForm2BillingInfo() {
      let cardData = getCardData();
      return {
        firstName: cardData.firstName,
        lastName: cardData.lastName,
        paymentMethod: {
          lastFour: cardData.cardNumber.slice(cardData.cardNumber.length - 4),
          expMonth: parseInt(cardData.month, 10),
          expYear: parseInt(cardData.year, 10),
        },
        address: {
          country: cardData.country,
          street1: cardData.street1,
          street2: cardData.street2,
          city: cardData.city,
          region: cardData.region,
          postalCode: cardData.zip,
        },
      };
    }

    function getCardData() {
      let expirationParts = cardExpiration.value
        .split("/")
        .map((str) => str.replace(/\s/g, ""))
        .map((str) => {
          if (str.length < 2) {
            return "0" + str;
          }
          return str;
        });

      return {
        firstName: billingInfo.value.firstName.replace(/\s/g, ""),
        lastName: billingInfo.value.lastName.replace(/\s/g, ""),
        cardNumber: cardNumber.value.replace(/\s/g, ""),
        month: expirationParts[0],
        year: expirationParts[1],
        cardCode: cardCVC.value.replace(/\s/g, ""),
        country: billingInfo.value.address.country,
        street1: billingInfo.value.address.street1,
        street2: billingInfo.value.address.street2,
        city: billingInfo.value.address.city.replace(/\s/g, ""),
        region: billingInfo.value.address.region,
        zip: billingInfo.value.address.postalCode.replace(/\s/g, ""),
      };
    }

    function tokenizeCard(): Promise<OpaqueTokenData> {
      return new Promise((resolve, reject) => {
        let cardData = getCardData();

        // @ts-ignore
        window.Accept.dispatchData(
          {
            authData: {
              clientKey: config.authorizeNet.publicClientKey,
              apiLoginID: config.authorizeNet.apiLoginId,
            },
            cardData: {
              cardNumber: cardData.cardNumber,
              month: cardData.month,
              year: cardData.year,
              cardCode: cardData.cardCode,
              zip: cardData.zip,
              fullName: `${cardData.firstName} ${cardData.lastName}`,
            },
          },
          (response: AuthorizeNetResponse) => {
            if (response.messages.resultCode === "Ok") {
              resolve(response.opaqueData);
            } else {
              reject(
                new Error(
                  response.messages.message.reduce((errorMessage, m) => {
                    if (errorMessage !== "") {
                      errorMessage += "\n";
                    }
                    return (errorMessage += `${m.code}:${m.text}`);
                  }, "")
                )
              );
            }
          }
        );
      });
    }

    return {
      errorMessage: states.errorMessage,
      loadingShow: states.loadingShow,
      countryList: countryList,
      cancelDisabled,
      billingInfo,
      cardNumber,
      cardExpiration,
      cardCVC,
      updateBillingInfo,
      toggleChangeEnabled
    };
  },
});
